import { CartAdd } from "@lazarentertainment/web-design-system/components/iconOutline"
import { Button } from "client/designSystem/Button"
import { SelectPortalWithFullscreenAwareness } from "client/designSystem/FullscreenAwarePortals"
import NumberInput from "client/designSystem/NumberInput"
import {
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectRoot,
  SelectTrigger,
} from "client/designSystem/SelectInput"
import {
  DetailedProductFieldsFragment,
  useProductQuery,
} from "client/graphql/storefront-api"
import { useAnimationContext } from "client/packages/animations/AnimationContext"
import { OverlaidAnimationStyle } from "client/packages/animations/types"
import { FormEventHandler, useEffect, useRef, useState } from "react"
import ResponsiveLoader from "shared/components/ResponsiveLoader"

import {
  getDefaultVariantForProduct,
  ProductVariant,
} from "../../utils/getDefaultVariantForProduct"
import { isProduct } from "../../utils/isProduct"
import { useCartContext, useShopifyCart } from "../cart/CartContext"
import { useShopContext } from "../ShopContext"
import { Price } from "../ui/priceComponents/Price"
import { SoldOut } from "../ui/priceComponents/SoldOut"

import ImageCarousel from "./ImageCarousel"
import ImageGallery from "./ImageGallery"

import styles from "./ProductDetails.module.css"

export const getQuantityAvailableForVariant = (
  variant: ProductVariant | null,
) => {
  if (variant === null) {
    return 0
  }
  const sellWhileOutOfStockMaximum = 100
  const { availableForSale, quantityAvailable } = variant

  // Inventory cannot be sold after item is unavailable.
  // Infer whether or not the product will continue selling when out of stock
  // to determine what the upper bound should be.
  const upperBound =
    quantityAvailable <= 0 && availableForSale
      ? sellWhileOutOfStockMaximum
      : quantityAvailable

  return availableForSale ? upperBound : 0
}

// RETRIEVES VARIANT BASED ON SELECTED OPTIONS
function getVariantForOptions(
  selectedProduct: DetailedProductFieldsFragment,
  productOptions: Record<string, string>,
): ProductVariant | null {
  const variantFound = selectedProduct.variants.nodes.find((variant) => {
    return variant.selectedOptions.every((option) => {
      return productOptions[option.name] === option.value
    })
  })
  return variantFound ?? null
}

function getVariantById(
  selectedProduct: DetailedProductFieldsFragment,
  variantId: string,
): ProductVariant | null {
  const variantFound = selectedProduct.variants.nodes.find((variant) => {
    return variant.id === variantId
  })
  return variantFound ?? null
}

const PRODUCT_FORM_ADD_TO_CART_BUTTON_VALUE = "add-to-cart"
const PRODUCT_FORM_BUY_NOW_BUTTON_VALUE = "buy-now"

export type ProductDetailsProps = {}

export const ProductDetails = () => {
  const {
    currentlySelectedProductId,
    setCurrentlySelectedVariantId,
    currentlySelectedVariantId,
    showImageGallery,
    setShowImageGallery,
    setAddedProduct,
  } = useShopContext()
  const { addItem, isLoading: shopifyCartLoading } = useShopifyCart()
  const { setOverlaidAnimation } = useAnimationContext()
  const { onBuyNowClick } = useCartContext()

  const { data: productNode, loading: productLoading } = useProductQuery({
    variables: { id: currentlySelectedProductId },
    context: { useStorefrontAPI: true },
    fetchPolicy: "cache-and-network",
    onCompleted: (data) => {
      if (isProduct(data.product) && currentlySelectedVariantId === null) {
        const defaultVariant = getDefaultVariantForProduct(data.product)
        setCurrentlySelectedVariantId(defaultVariant.id)
      }
    },
  })

  const product = productNode?.product as
    | DetailedProductFieldsFragment
    | undefined

  const variant =
    product && currentlySelectedVariantId
      ? getVariantById(product, currentlySelectedVariantId)
      : null
  const quantityAvailable = getQuantityAvailableForVariant(variant)

  const [initialImageGalleryIndex, setInitialImageGalleryIndex] =
    useState(0)

  const [options, setOptions] = useState({})
  const quantityInputRef = useRef<HTMLInputElement>(null)

  // When a variant option is selected, find & select the corresponding variant
  const onValueChange = (option: string, newValue: string) => {
    const newVariant =
      getVariantForOptions(product, { ...options, [option]: newValue })
        ?.id ?? null
    setCurrentlySelectedVariantId(newVariant)
    if (newVariant === null) {
      setOptions({ ...options, [option]: newValue })
    }
  }

  // When a variant is selected, populate our `options` state
  useEffect(() => {
    if (!variant) {
      return
    }
    setOptions(
      variant?.selectedOptions.reduce(
        (optionsMap, selectedOption) => {
          return {
            ...optionsMap,
            [selectedOption.name]: selectedOption.value,
          }
        },
        {} as Record<string, string>,
      ),
    )
  }, [variant])

  const onSubmit: FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault()
    if (shopifyCartLoading) {
      return
    }

    const quantity = quantityInputRef.current?.value ?? "1"
    const numberQuantity = parseInt(quantity, 10)

    // There are two ways to submit this form: Add to cart, or buy now.
    // We do this through the form's onSubmit so we get automatic form validation for both cases.
    // The only way to have two paths through the onSubmit (that I've found) is to
    // check for SubmitEvent's `submitter`. In this case we have a SubmitEvent but React's types aren't aware of it.
    // So we're casting this nativeEvent to SubmitEvent and then checking it in a typesafe way.
    const submitEvent = event.nativeEvent as SubmitEvent
    if (
      "name" in submitEvent?.submitter &&
      submitEvent.submitter.name === PRODUCT_FORM_BUY_NOW_BUTTON_VALUE
    ) {
      onBuyNowClick(variant.id, numberQuantity)
    } else {
      addItem(variant, numberQuantity).then((data) => {
        const quantityAdded =
          data.data?.cartLinesAdd?.cart?.lines?.nodes?.find((lineItem) => {
            return lineItem.merchandise.id === variant.id
          })?.quantity ?? 0
        if (quantityAdded === 0) {
          return
        }
        setAddedProduct({
          variant,
          quantity: quantityAdded,
        })
        setOverlaidAnimation(OverlaidAnimationStyle.HEARTS)
      })
    }
  }

  if (productLoading) {
    return <ResponsiveLoader />
  }

  if (showImageGallery) {
    return (
      <ImageGallery
        product={product}
        initialIndex={initialImageGalleryIndex}
        onBack={() => setShowImageGallery(false)}
      />
    )
  }

  return (
    <div className={styles.productDetails}>
      <ImageCarousel
        product={product}
        openImageGallery={(initialIndex = 0) => {
          setShowImageGallery(true)
          setInitialImageGalleryIndex(initialIndex)
        }}
      />
      <div className={styles.productDetailsTitleSection}>
        <h2 className="title-md text-color-primary">{product.title}</h2>
        {variant === null && <SoldOut unavailable />}
        {variant !== null && (
          <>
            {quantityAvailable > 0 ? (
              <Price
                amount={variant.price.amount}
                currencyCode={variant.price.currencyCode}
              />
            ) : (
              <SoldOut />
            )}
          </>
        )}
      </div>
      <form className={styles.productDetailsForm} onSubmit={onSubmit}>
        {product.options.map((option) => {
          if (option.values.length === 1) {
            return null
          }
          return (
            <SelectRoot
              key={option.name}
              required
              value={options[option.name]}
              onValueChange={(newValue: string) =>
                onValueChange(option.name, newValue)
              }
            >
              <SelectTrigger
                id={`optionsmenu-${option.name}`}
                label={option.name}
              />
              <SelectPortalWithFullscreenAwareness>
                <SelectContent>
                  <SelectGroup>
                    {option.values.map((value) => {
                      return <SelectItem value={value}>{value}</SelectItem>
                    })}
                  </SelectGroup>
                </SelectContent>
              </SelectPortalWithFullscreenAwareness>
            </SelectRoot>
          )
        })}
        <NumberInput
          ref={quantityInputRef}
          id="quantity"
          label="Quantity"
          defaultValue={1}
          min={1}
          disabled={quantityAvailable <= 0}
          required
        />
        <div className={styles.productDetailsBottomSection}>
          <Button
            variant="secondary"
            icon={CartAdd}
            type="submit"
            name={PRODUCT_FORM_ADD_TO_CART_BUTTON_VALUE}
            disabled={quantityAvailable <= 0}
            data-robot="product-details-add-to-cart"
            loading={shopifyCartLoading}
          >
            Add to cart
          </Button>
          <Button
            variant="primary"
            type="submit"
            name={PRODUCT_FORM_BUY_NOW_BUTTON_VALUE}
            disabled={quantityAvailable <= 0}
            data-robot="product-details-buy-now"
          >
            Buy now
          </Button>
        </div>
      </form>
    </div>
  )
}
